/** Admin only. Secrets are set here and never read back. */

import { useEffect, useState } from 'react'
import {
  Body1Strong,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Switch,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import {
  CollapsibleSection,
  EmptyState,
  FormSection,
  KeyValueList,
  KeyValueRow,
  LoadingState,
  MonoText,
  SectionCard,
  formatTimestamp,
} from '../platform'
import { ApiError, api } from '../api'
import type { AlertTestResult, Settings as SettingsData } from '../api'
import { useAuth } from '../auth'
import { availableThemes } from '../theme'
import { useThemeChoice } from '../themeContext'
import SettingsConnections, { Valued } from './SettingsConnections'
import StoragePanel from './StoragePanel'
import {
  daysProblem,
  emailProblem,
  hostProblem,
  noSpacesProblem,
  portProblem,
  recipientsProblem,
  senderProblem,
} from './fieldRules'

/** Every alert the service knows. Listed here so a rule that exists on the
 *  service but not in this list cannot quietly disappear when the form saves.
 *  The service's tests read this list by `id`. */
const TRIGGERS = [
  {
    id: 'unstamped_start',
    name: 'Session start with no authorization',
    what: 'A session started at an eligible charge point with no authorization on record.',
  },
  {
    id: 'ambiguous_stop',
    name: 'Stop matching several sessions',
    what: 'A stop matched more than one session, so it is not known which one ended.',
  },
  {
    id: 'daily_snapshot',
    name: 'Daily summary',
    what: 'Sent just after 07:00 UTC, for the 24 hours before.',
  },
  {
    id: 'refused_an_approval',
    name: 'Approval refused by this service',
    what: 'An authority approved a card and this service then refused it.',
  },
  {
    id: 'hubject_session_held',
    name: 'Session left open at Hubject',
    what: 'Hubject holds a session for an authorization that never completed.',
  },
  {
    id: 'hubject_reconcile_blind',
    name: 'Hubject cannot be asked about open sessions',
    what: 'The check for sessions left open at Hubject could not run.',
  },
  {
    id: 'hubject_credential_refused',
    name: 'Hubject connector key refused',
    what: 'The connector refused the API key. Sessions on it wait until a new key is set.',
  },
  {
    id: 'station_list_stale',
    name: 'Station list out of date',
    what: 'The station list has not been updated for an hour.',
  },
  {
    id: 'approved_session_unbound',
    name: 'Approved session with no stamp',
    what: 'TIM or Hubject approved a card and its session started with no stamp, so nobody bills it.',
  },
  {
    id: 'unowned_events_spike',
    name: 'Unowned events jumped',
    what: 'Session starts and stops nobody owns: at least 20 in the last hour and 3 times the hourly rate of the day before.',
  },
  {
    id: 'authority_unhealthy',
    name: 'Connection degraded or down',
    what: 'A connection reports itself degraded or down.',
    notYet: 'nothing checks connection health in the background.',
  },
  {
    id: 'location_tree_fail_closed',
    name: 'Eligibility cannot be determined',
    what: 'Eligibility cannot be determined, so requests are refused.',
    notYet: 'there is no location tree.',
  },
  {
    id: 'location_tree_refresh_failed',
    name: 'Location tree refresh failed',
    what: 'The location tree could not be refreshed.',
    notYet: 'there is no location tree.',
  },
  {
    id: 'refusal_rate_anomaly',
    name: 'Refusal spike',
    what: 'Refusals rose sharply against the usual rate.',
    notYet: 'no usual rate is defined.',
  },
] as const

/**
 * Whether anything in the service sends this alert.
 *
 * A service too old to say has every alert treated as sent, because refusing
 * a subscription the service would honour is worse than offering one.
 */
export function isRaised(trigger: string, raised: readonly string[] | undefined): boolean {
  return raised === undefined || raised.includes(trigger)
}

const useStyles = makeStyles({
  delay: { margin: 0, color: tokens.colorNeutralForeground2 },
  // Bounded boxes in a grid. Two up on a desktop, one up on a phone, decided
  // by the content's own minimum. Cards keep their own height.
  page: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    gap: tokens.spacingHorizontalM,
    alignItems: 'start',
  },
  wide: { gridColumn: '1 / -1' },
  stacked: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: tokens.spacingHorizontalM,
    alignItems: 'start',
  },
  rules: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  rule: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  ruleFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalM,
    alignItems: 'end',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  // A status colour from the theme: raw hex may only live in platform/palette.ts.
  warn: { color: tokens.colorStatusWarningForeground1 },
  failed: { color: tokens.colorStatusDangerForeground1 },
  note: { color: tokens.colorNeutralForeground3 },
  narrow: { maxWidth: '160px' },
})

/** The service's own sentence for a refusal, which is in `detail`. */
function refusal(e: unknown): string {
  if (e instanceof ApiError) return e.problem.detail || e.message
  return e instanceof Error ? e.message : 'The request failed.'
}

interface Said {
  ok: boolean
  line: string
}

function SaidLine({ said }: { said: Said | null }) {
  const styles = useStyles()
  if (!said) return null
  return (
    <Caption1 role="status" className={said.ok ? styles.note : styles.failed}>
      {said.line}
    </Caption1>
  )
}

export default function Settings() {
  const styles = useStyles()
  const { principal } = useAuth()
  const isAdmin = principal?.role === 'admin'
  const [data, setData] = useState<SettingsData | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () =>
    api
      .get<SettingsData>('/settings')
      .then(setData)
      .catch((e) => setError(refusal(e)))

  useEffect(() => {
    if (isAdmin) void load()
  }, [isAdmin])

  if (!isAdmin) {
    return (
      <div className={styles.page}>
        <SectionCard title="Your access" description="Read only.">
          <KeyValueList divided>
            <KeyValueRow label="Email" value={<MonoText>{principal?.email ?? ''}</MonoText>} />
            <KeyValueRow label="Role" value={principal?.role ?? 'viewer'} />
          </KeyValueList>
        </SectionCard>
        <AppearanceSettings />
      </div>
    )
  }

  // Only a failed READ replaces the page. A refused save is said where it
  // was pressed, and the page stays.
  if (error) return <EmptyState title="The settings cannot be read" description={error} />
  if (!data) return <LoadingState label="Reading the settings" />

  return (
    <div className={styles.page}>
      {data.takes_effect_within_seconds ? (
        <p className={styles.delay}>
          A switch or a key saved here reaches every authorization within{' '}
          {data.takes_effect_within_seconds} seconds.
        </p>
      ) : null}
      <SettingsConnections data={data} onChanged={load} />
      {/* A section missing from the answer renders as absent: the console and
          the service are briefly out of step on every deploy. */}
      {data.inbound ? <IncomingKey inbound={data.inbound} onChanged={load} /> : null}
      {data.inbound ? (
        <CallerKeys
          inbound={data.inbound}
          onChanged={load}
          name="wall_e_list_api_key"
          title="Wall-E list key (OCPP Admin)"
          description="The key the OCPP Admin sends to read and change the Wall-E list. Not the OCPP Server's key."
          saved="Saved. Give the OCPP Admin the same value."
        />
      ) : null}
      {data.inbound ? (
        <CallerKeys
          inbound={data.inbound}
          onChanged={load}
          name="canary_key"
          title="Canary key"
          description="The key the canary runner sends beside the OCPP Server's key, to check a revision before it takes traffic."
          saved="Saved. Put the same value where the canary runner reads it."
        />
      ) : null}
      {data.mail ? <Mail mail={data.mail} onChanged={load} /> : null}
      <Alerts data={data} onChanged={load} />
      {/* Together and across the width: the table needs it, and what is
          stored is what the retention window above it does not delete yet. */}
      <div className={styles.stacked}>
        <Retention days={data.retention_days ?? 30} onChanged={load} />
        <StoragePanel />
      </div>
      {data.sign_in ? <SignIn signIn={data.sign_in} /> : null}
      <AppearanceSettings />
    </div>
  )
}

interface Changes {
  onChanged: () => Promise<unknown>
}

function IncomingKey({
  inbound,
  onChanged,
}: Changes & { inbound: SettingsData['inbound'] }) {
  const styles = useStyles()
  const [key, setKey] = useState('')
  const [said, setSaid] = useState<Said | null>(null)

  const save = () => {
    setSaid(null)
    api
      .put('/settings', { inbound: { broker_api_key: key } })
      .then(() => {
        setKey('')
        setSaid({ ok: true, line: 'Saved. Give the OCPP Server the same value.' })
        return onChanged()
      })
      .catch((e) => setSaid({ ok: false, line: `Not saved: ${refusal(e)}` }))
  }

  return (
    <FormSection
      title="Incoming API key (OCPP Server)"
      description="The key the OCPP Server sends with every call to this service."
      footer={
        <div className={styles.actions}>
          <Button appearance="primary" onClick={save} disabled={!key}>
            Save
          </Button>
          <SaidLine said={said} />
        </div>
      }
    >
      <KeyValueList divided>
        <KeyValueRow
          label="API key"
          value={
            <Valued
              source={
                !inbound.broker_api_key_set
                  ? 'deployment'
                  : inbound.broker_api_key_updated_at
                    ? `console, changed ${formatTimestamp(inbound.broker_api_key_updated_at)}`
                    : 'console'
              }
            >
              {inbound.broker_api_key_set ? 'set' : 'not set in the console'}
            </Valued>
          }
        />
        <KeyValueRow label="Endpoint" value={<MonoText>{inbound.external_api_path}</MonoText>} />
        <KeyValueRow label="Events" value={<MonoText>{(inbound.events ?? []).join(', ')}</MonoText>} />
      </KeyValueList>
      <Field
        label="New API key"
        hint="The OCPP Server must be given the same value. Leave empty to keep the current one."
      >
        <Input
          type="password"
          autoComplete="off"
          value={key}
          onChange={(_, d) => setKey(d.value)}
        />
      </Field>
    </FormSection>
  )
}

/** A key a caller presents to this service, and its rotation slot: the OCPP
 *  Admin's for the Wall-E list (DEC-28) and the canary runner's (DEC-29).
 *  Write only, like every key here: the page says whether each is set and
 *  when, and never what it is. */
function CallerKeys({
  inbound,
  onChanged,
  name,
  title,
  description,
  saved,
}: Changes & {
  inbound: SettingsData['inbound']
  name: 'wall_e_list_api_key' | 'canary_key'
  title: string
  description: string
  saved: string
}) {
  const styles = useStyles()
  const [key, setKey] = useState('')
  const [next, setNext] = useState('')
  const [said, setSaid] = useState<Said | null>(null)

  const put = (fields: Record<string, string>, line: string) => {
    setSaid(null)
    api
      .put('/settings', { inbound: fields })
      .then(() => {
        setKey('')
        setNext('')
        setSaid({ ok: true, line })
        return onChanged()
      })
      .catch((e) => setSaid({ ok: false, line: `Not saved: ${refusal(e)}` }))
  }
  const slot = `${name}_next` as const
  const fields: Record<string, string> = {}
  if (key) fields[name] = key
  if (next) fields[slot] = next
  const state = (set: boolean | undefined, at: number | null | undefined) =>
    set ? (at ? `set, changed ${formatTimestamp(at)}` : 'set') : 'not set'

  return (
    <FormSection
      title={title}
      description={description}
      footer={
        <div className={styles.actions}>
          <Button appearance="primary" onClick={() => put(fields, saved)} disabled={!key && !next}>
            Save
          </Button>
          <Button
            onClick={() => put({ [slot]: '' }, 'The next key is cleared.')}
            disabled={!inbound[`${slot}_set`]}
          >
            Clear the next key
          </Button>
          <SaidLine said={said} />
        </div>
      }
    >
      <KeyValueList divided>
        <KeyValueRow label="Key" value={state(inbound[`${name}_set`], inbound[`${name}_updated_at`])} />
        <KeyValueRow
          label="Next key"
          value={state(inbound[`${slot}_set`], inbound[`${slot}_updated_at`])}
        />
      </KeyValueList>
      <Field label="New key" hint="16 to 256 characters, no spaces. Leave empty to keep the current one.">
        <Input type="password" autoComplete="off" value={key} onChange={(_, d) => setKey(d.value)} />
      </Field>
      <Field label="Next key" hint="Accepted beside the key while it is set, for a rotation.">
        <Input type="password" autoComplete="off" value={next} onChange={(_, d) => setNext(d.value)} />
      </Field>
    </FormSection>
  )
}

function Mail({ mail, onChanged }: Changes & { mail: SettingsData['mail'] }) {
  const styles = useStyles()
  const [form, setForm] = useState({
    host: mail.host ?? '',
    port: mail.port ? String(mail.port) : '',
    username: mail.username ?? '',
    sender: mail.default_sender ?? '',
  })
  const [password, setPassword] = useState('')
  const [recipient, setRecipient] = useState('')
  const [said, setSaid] = useState<Said | null>(null)

  const problems = {
    host: hostProblem(form.host),
    port: portProblem(form.port),
    username: noSpacesProblem(form.username),
    sender: senderProblem(form.sender),
  }
  const wrong = Object.values(problems).some(Boolean)
  const recipientSaid = emailProblem(recipient)

  const save = () => {
    setSaid(null)
    api
      .put('/settings', {
        mail: {
          host: form.host.trim(),
          port: form.port ? Number(form.port) : null,
          username: form.username.trim(),
          default_sender: form.sender.trim(),
          // Omitted when empty, which leaves the stored one alone.
          ...(password ? { password } : {}),
        },
      })
      .then(() => {
        setPassword('')
        setSaid({ ok: true, line: 'Saved. In use from the next alert.' })
        return onChanged()
      })
      .catch((e) => setSaid({ ok: false, line: `Not saved: ${refusal(e)}` }))
  }

  const sendTest = () => {
    setSaid(null)
    api
      .post<{ succeeded: boolean; detail: string | null }>('/settings/mail/test', {
        // What is in the form, not what is stored, so a server can be tested
        // before it is saved.
        host: form.host || null,
        port: form.port ? Number(form.port) : null,
        username: form.username || null,
        password: password || null,
        sender: form.sender || null,
        recipient,
      })
      .then((r) =>
        setSaid(
          r.succeeded
            ? { ok: true, line: 'Test mail sent.' }
            : { ok: false, line: `Test mail failed: ${r.detail ?? 'no reason given'}` },
        ),
      )
      .catch((e) => setSaid({ ok: false, line: `Test mail failed: ${refusal(e)}` }))
  }

  const last = mail.last_test
  return (
    <CollapsibleSection
      title="Mail"
      // Open when there is something to do: with no server, no alert is sent.
      defaultOpen={!mail.host}
      description={
        mail.host
          ? `${mail.host}:${mail.port ?? 587}`
          : 'No server set. No alert can be sent.'
      }
    >
      <FormSection
        title=""
        footer={
          <div className={styles.actions}>
            <Button appearance="primary" onClick={save} disabled={!form.host || wrong}>
              Save
            </Button>
            <Button
              onClick={sendTest}
              disabled={!recipient || Boolean(recipientSaid) || !form.host || wrong}
            >
              Send test
            </Button>
            <SaidLine said={said} />
            {last ? (
              <Caption1 className={styles.note}>
                Last test: {formatTimestamp(last.attempted_at)}, {last.succeeded ? 'sent' : 'failed'}
                {last.detail ? `, ${last.detail}` : ''}
              </Caption1>
            ) : null}
          </div>
        }
      >
        <div className={styles.fields}>
          <Field
            label="SMTP server"
            hint="Host name, without http:// and without a port. Example: smtp.ionos.de"
            validationMessage={problems.host ?? undefined}
          >
            <Input value={form.host} onChange={(_, d) => setForm({ ...form, host: d.value })} />
          </Field>
          <Field
            label="Port"
            hint="587 for STARTTLS, 465 for SSL. Example: 587"
            validationMessage={problems.port ?? undefined}
          >
            <Input value={form.port} onChange={(_, d) => setForm({ ...form, port: d.value })} />
          </Field>
          <Field
            label="Username"
            hint="The account that signs in to the server. Example: wle-eas@wall-e.one"
            validationMessage={problems.username ?? undefined}
          >
            <Input
              value={form.username}
              onChange={(_, d) => setForm({ ...form, username: d.value })}
            />
          </Field>
          <Field
            label="Password"
            hint={`The password of that account. ${
              mail.password_set
                ? mail.password_updated_at
                  ? `Set, changed ${formatTimestamp(mail.password_updated_at)}. Leave empty to keep it.`
                  : 'Set. Leave empty to keep it.'
                : 'Not set.'
            }`}
          >
            <Input
              type="password"
              autoComplete="off"
              value={password}
              onChange={(_, d) => setPassword(d.value)}
            />
          </Field>
          <Field
            label="Sender"
            hint="The address alerts come from, with or without a display name. Example: Wall-E EA Service <wle-eas@wall-e.one>"
            validationMessage={problems.sender ?? undefined}
          >
            <Input value={form.sender} onChange={(_, d) => setForm({ ...form, sender: d.value })} />
          </Field>
          <Field
            label="Test recipient"
            hint="Send test mails this address using the values above, saved or not. Example: name@wallefleet.de"
            validationMessage={recipientSaid ?? undefined}
          >
            <Input value={recipient} onChange={(_, d) => setRecipient(d.value)} />
          </Field>
        </div>
      </FormSection>
    </CollapsibleSection>
  )
}

interface Rule {
  trigger: string
  /** As typed, so a comma can be typed. Split when saved. */
  typed: string
  enabled: boolean
}

const recipientsOf = (typed: string) =>
  typed
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)

function Alerts({ data, onChanged }: Changes & { data: SettingsData }) {
  const styles = useStyles()
  const [rules, setRules] = useState<Rule[]>(() => {
    // Start from what the service holds, then add the alerts it has no rule
    // for, so the form shows every alert that exists.
    const held = new Map((data.alerts ?? []).map((a) => [a.trigger, a]))
    return TRIGGERS.map(({ id }) => {
      const rule = held.get(id)
      return {
        trigger: id,
        typed: (rule?.recipients ?? []).join(', '),
        enabled: rule?.enabled ?? false,
      }
    })
  })
  const [said, setSaid] = useState<Said | null>(null)
  const [testing, setTesting] = useState<string | null>(null)
  // Keyed by alert so two rows cannot show each other's result.
  const [tested, setTested] = useState<Record<string, Said>>({})

  const raised = (trigger: string) => isRaised(trigger, data.alerts_raised)
  // The service's own count, computed with the function the sender uses.
  const reaches = (trigger: string) => data.alerts_reach?.[trigger] ?? 0
  const mustReach = (trigger: string) => (data.alerts_must_reach ?? []).includes(trigger)
  const update = (trigger: string, change: Partial<Rule>) =>
    setRules((current) => current.map((r) => (r.trigger === trigger ? { ...r, ...change } : r)))

  const wrong = rules.some((r) => raised(r.trigger) && recipientsProblem(r.typed))
  const sent = rules.filter((r) => raised(r.trigger))
  const on = sent.filter((r) => r.enabled).length
  // From what is stored, and a missing rule counts: no rule sends to nobody.
  const unstampedOff = !(data.alerts ?? []).find((r) => r.trigger === 'unstamped_start')?.enabled

  const save = () => {
    setSaid(null)
    api
      .put('/settings', {
        // Only rules with a recipient are stored. A rule that is on with no
        // recipient sends to nobody and looks covered.
        alerts: rules
          .map((r) => ({
            trigger: r.trigger,
            recipients: recipientsOf(r.typed),
            enabled: r.enabled,
          }))
          .filter((r) => r.recipients.length > 0),
      })
      .then(() => {
        setSaid({ ok: true, line: 'Saved.' })
        return onChanged()
      })
      .catch((e) => setSaid({ ok: false, line: `Not saved: ${refusal(e)}` }))
  }

  const sendTest = (trigger: string) => {
    setTesting(trigger)
    setTested(({ [trigger]: _gone, ...rest }) => rest)
    api
      .post<AlertTestResult>(`/settings/alerts/${trigger}/test`, {})
      .then((r) =>
        setTested((t) => ({
          ...t,
          // The count and not the addresses: a result is pasted into tickets.
          [trigger]: r.sent
            ? {
                ok: true,
                line: `Test sent to ${r.recipients} ${r.recipients === 1 ? 'person' : 'people'}. Check the inbox.`,
              }
            : { ok: false, line: `Nothing sent: ${r.detail ?? 'this alert has no recipient'}` },
        })),
      )
      .catch((e) =>
        setTested((t) => ({ ...t, [trigger]: { ok: false, line: `Nothing sent: ${refusal(e)}` } })),
      )
      .finally(() => setTesting(null))
  }

  return (
    <>
      {/* Outside the collapsed section: nobody opens a section to find out
          whether there is something to worry about. */}
      {unstampedOff ? (
        <MessageBar className={styles.wide} intent="warning">
          <MessageBarBody>
            <MessageBarTitle>
              The alert for a session start with no authorization is off
            </MessageBarTitle>
            A session like that is not billed. Turn the alert on to be told when it happens.
          </MessageBarBody>
        </MessageBar>
      ) : null}
      <div className={styles.wide}>
      <CollapsibleSection
        title="Alerts"
        description={`${on} of ${sent.length} on. ${rules.length - sent.length} not sent yet.`}
      >
        <div className={styles.rules}>
          {rules.map((rule) => {
            const meta = TRIGGERS.find((t) => t.id === rule.trigger)
            const isSent = raised(rule.trigger)
            const notYet = meta && 'notYet' in meta ? meta.notYet : 'nothing in the service sends it.'
            const typedSaid = isSent ? recipientsProblem(rule.typed) : null
            const told = reaches(rule.trigger)
            return (
              <div
                key={rule.trigger}
                className={styles.rule}
                role="group"
                aria-label={meta?.name ?? rule.trigger}
              >
                <Body1Strong>{meta?.name ?? rule.trigger}</Body1Strong>
                <Caption1 className={styles.note}>
                  {meta?.what ?? rule.trigger}
                  {isSent ? '' : ` Not sent yet: ${notYet}`}
                </Caption1>
                <div className={styles.ruleFields}>
                  <Field
                    label="Recipients"
                    hint="Email addresses, separated by commas. Example: ops@wall-e.one, name@wallefleet.de"
                    validationMessage={typedSaid ?? undefined}
                  >
                    <Input
                      disabled={!isSent}
                      value={rule.typed}
                      onChange={(_, d) => update(rule.trigger, { typed: d.value })}
                    />
                  </Field>
                  <Switch
                    disabled={!isSent}
                    checked={isSent && rule.enabled}
                    onChange={(_, d) => update(rule.trigger, { enabled: d.checked })}
                    label={isSent ? 'Send this alert' : 'Not sent yet'}
                  />
                </div>
                {isSent ? (
                  <div className={styles.actions}>
                    <Caption1 className={told ? styles.note : mustReach(rule.trigger) ? styles.failed : styles.warn}>
                      {told
                        ? `Sent to ${told} ${told === 1 ? 'person' : 'people'}.`
                        : 'No recipient: nobody is told.'}
                    </Caption1>
                    <Button
                      size="small"
                      disabled={testing === rule.trigger}
                      onClick={() => sendTest(rule.trigger)}
                    >
                      {testing === rule.trigger ? 'Sending' : 'Send test'}
                    </Button>
                    <SaidLine said={tested[rule.trigger] ?? null} />
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
        <div className={styles.actions}>
          <Button appearance="primary" onClick={save} disabled={wrong}>
            Save
          </Button>
          <SaidLine said={said} />
        </div>
      </CollapsibleSection>
      </div>
    </>
  )
}

function Retention({ days, onChanged }: Changes & { days: number }) {
  const styles = useStyles()
  const [typed, setTyped] = useState(String(days))
  const [said, setSaid] = useState<Said | null>(null)
  const typedSaid = daysProblem(typed)

  const save = () => {
    setSaid(null)
    api
      .put('/settings', { retention_days: Number(typed) })
      .then(() => {
        setSaid({ ok: true, line: 'Saved.' })
        return onChanged()
      })
      .catch((e) => setSaid({ ok: false, line: `Not saved: ${refusal(e)}` }))
  }

  return (
    <FormSection
      title="Retention"
      footer={
        <div className={styles.actions}>
          <Button appearance="primary" onClick={save} disabled={Boolean(typedSaid)}>
            Save
          </Button>
          <SaidLine said={said} />
        </div>
      }
    >
      {/* The window is stored and nothing deletes yet: the sweep exists and
          nothing schedules it (FIND-05). The field says so, because a control
          that is shown and not applied is one somebody relies on. */}
      <Field
        label="Retention (days)"
        hint="How long the communications log is kept, 1 to 3650 days. Nothing is deleted yet: the cleanup job is not scheduled."
        validationMessage={typedSaid ?? undefined}
      >
        <Input
          className={styles.narrow}
          value={typed}
          onChange={(_, d) => setTyped(d.value)}
        />
      </Field>
    </FormSection>
  )
}

function SignIn({ signIn }: { signIn: NonNullable<SettingsData['sign_in']> }) {
  return (
    <SectionCard title="Sign-in" description="Set by the deployment. Read only.">
      <KeyValueList divided>
        <KeyValueRow
          label="Tenant ID"
          value={signIn.tenant_id ? <MonoText>{signIn.tenant_id}</MonoText> : 'not set'}
        />
        <KeyValueRow
          label="Application ID"
          value={
            signIn.client_id ? (
              <MonoText>{signIn.client_id}</MonoText>
            ) : (
              'not set: Microsoft sign-in is off'
            )
          }
        />
        <KeyValueRow label="Password sign-in" value={signIn.password_enabled ? 'on' : 'off'} />
        <KeyValueRow
          label="Development bypass"
          value={signIn.development_bypass ? 'on: this environment is not authenticated' : 'off'}
        />
      </KeyValueList>
    </SectionCard>
  )
}

function AppearanceSettings() {
  const { themeId, chooseTheme } = useThemeChoice()
  return (
    <FormSection title="Appearance" description="Saved in this browser only.">
      <Field label="Theme">
        <Dropdown
          value={availableThemes().find((theme) => theme.id === themeId)?.name ?? themeId}
          selectedOptions={[themeId]}
          onOptionSelect={(_, data) => data.optionValue && chooseTheme(data.optionValue)}
        >
          {availableThemes().map((theme) => (
            <Option key={theme.id} value={theme.id}>
              {theme.name}
            </Option>
          ))}
        </Dropdown>
      </Field>
    </FormSection>
  )
}
